// HTTP API client for Mapas KM backend
import type { User, RegisterData, RouteGeneration, DashboardStats, Report, Town, ApiResponse } from '@/types'

interface ApiConfig {
  baseURL: string
  withCredentials: boolean
}

const apiConfig: ApiConfig = {
  baseURL: '', // Uses relative URLs for nginx proxy
  withCredentials: true
}

class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: any,
    public code?: string
  ) {
    super(message)
    this.name = 'ApiError'
    this.code = code
  }
}

class ApiClient {
  private baseURL: string
  private defaultHeaders: Record<string, string>

  constructor(config: ApiConfig) {
    this.baseURL = config.baseURL
    this.defaultHeaders = {
      'Content-Type': 'application/json'
    }
  }

  private getAuthHeader(): Record<string, string> {
    const token = localStorage.getItem('auth_token')
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`

    const config: RequestInit = {
      ...options,
      headers: {
        ...this.defaultHeaders,
        ...this.getAuthHeader(),
        ...options.headers
      },
      credentials: apiConfig.withCredentials ? 'include' : 'omit'
    }

    try {
      const response = await fetch(url, config)

      if (!response.ok) {
        let errorData: any
        try {
          const errorText = await response.text()
          errorData = JSON.parse(errorText)
        } catch {
          errorData = { message: `HTTP ${response.status}: ${response.statusText}` }
        }

        const errorMessage = errorData?.error?.message || errorData?.message || `HTTP ${response.status}: ${response.statusText}`
        const errorCode = errorData?.code || errorData?.error?.code

        const apiError = new ApiError(errorMessage, response.status, errorData, errorCode)
        apiError.response = { data: errorData }
        throw apiError
      }

      // Handle empty responses (e.g., 204 No Content)
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return {} as T
      }

      const contentType = response.headers.get('content-type')
      if (contentType?.includes('application/json')) {
        return await response.json()
      }

      // For file downloads or other non-JSON responses
      return response as unknown as T
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }
      throw new ApiError(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' })
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    })
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    })
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }
}

// Create singleton API client
export const api = new ApiClient(apiConfig)

// Authentication API
export const authApi = {
  async login(nif: string, password: string): Promise<{ token: string; user: User }> {
    return api.post('/api/auth/login', { nif, password })
  },

  async register(data: RegisterData): Promise<{ token: string; user: User }> {
    const payload = {
      name: `${data.firstName} ${data.lastName}`,
      company_name: data.companyName,
      email: data.email,
      password: data.password,
      nif: data.nif,
      source_address: data.source_address,
      source_lat: data.source_lat,
      source_lng: data.source_lng,
      vehicle_type: 'CAR' // Hardcoded as per spec
    }
    return api.post('/api/v1/auth/register', payload)
  },

  async logout(): Promise<void> {
    localStorage.removeItem('auth_token')
    // Could also call a logout endpoint if needed
  }
}

// Dashboard API
export const dashboardApi = {
  async getStatistics(): Promise<DashboardStats> {
    const response = await api.get<ApiResponse<{ statistics: DashboardStats }>>('/api/statistics/dashboard?period=current_month')
    return response.data.statistics
  },

  async getReports(): Promise<Report[]> {
    const response = await api.get<ApiResponse<{ reports: Report[] }>>('/api/reports')
    return response.data.reports
  },

  async getRouteGenerations(): Promise<RouteGeneration[]> {
    const response = await api.get<ApiResponse<{ generations: RouteGeneration[] }>>('/api/routes')
    return response.data.generations
  }
}

// Route Generation API
export const routeApi = {
  async createGeneration(data: {
    target_amount: number
    radius_km?: number  // Optional - backend auto-determines optimal radius
    month: number
    year: number
    algorithm?: string
    home_city_id?: number
  }): Promise<{ generation_id: number }> {
    const payload = {
      ...data,
      algorithm: data.algorithm || 'dynamic'
    }
    const response = await api.post<ApiResponse<{ generation_id: number }>>('/api/routes', payload)
    return response.data
  },

  async checkGenerationAccess(): Promise<{
    success: boolean
    message?: string
    data?: {
      can_generate: boolean
      subscription_status: string
      available_months: Array<{
        month: number
        year: number
        can_access: boolean
        reason: string
      }>
      has_used_free_report: boolean
      redirect_to_pricing?: boolean
    }
  }> {
    const response = await api.get<any>('/api/routes/check-access')
    return response
  },

  async downloadExcel(generationId: number): Promise<void> {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`/api/routes/${generationId}/export`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      throw new ApiError('Failed to download excel file')
    }

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `relatorio-km-${generationId}.xlsx`
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1]
      }
    }

    // Create blob and trigger download
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    a.remove()
  },

  async downloadAjudasCusto(generationId: number): Promise<void> {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`/api/routes/${generationId}/export-ajudas-custo`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      throw new ApiError('Failed to download ajudas de custo file')
    }

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `ajudas-custo-${generationId}.xlsx`
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1]
      }
    }

    // Create blob and trigger download
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    a.remove()
  },

  // ============================================================
  // ASYNC EXPORT FUNCTIONS - Background job processing
  // ============================================================

  /**
   * Start an async export job (returns immediately, processes in background)
   */
  async startExportAsync(generationId: number, exportType: 'mapa_km' | 'ajudas_custo' = 'mapa_km'): Promise<{
    success: boolean
    message: string
    data?: {
      job_id: string
      status: string
      poll_url: string
    }
  }> {
    return api.post(`/api/routes/${generationId}/export-async`, { export_type: exportType })
  },

  /**
   * Check the status of an export job
   */
  async getExportJobStatus(jobId: string): Promise<{
    success: boolean
    data?: {
      job_id: string
      status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled'
      progress: number
      error_message?: string
      filename?: string
      created_at: string
      completed_at?: string
    }
  }> {
    return api.get(`/api/export-jobs/${jobId}/status`)
  },

  /**
   * Download a completed export job
   */
  async downloadExportJob(jobId: string): Promise<void> {
    const token = localStorage.getItem('auth_token')
    const response = await fetch(`/api/export-jobs/${jobId}/download`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      throw new ApiError('Ficheiro não disponível para download')
    }

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = 'download.zip'
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1]
      }
    }

    // Create blob and trigger download
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    a.remove()
  },

  /**
   * Download Excel with async processing (for large exports with multiple colaboradores)
   * Shows progress and handles the background job automatically
   */
  async downloadExcelAsync(
    generationId: number,
    onProgress?: (progress: number, status: string) => void
  ): Promise<void> {
    // Start the async export job
    const startResult = await this.startExportAsync(generationId, 'mapa_km')

    if (!startResult.success || !startResult.data?.job_id) {
      throw new ApiError(startResult.message || 'Falha ao iniciar exportação')
    }

    const jobId = startResult.data.job_id

    // Poll for completion
    const maxAttempts = 120 // 2 minutes max (1 second intervals)
    let attempts = 0

    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 1000)) // Wait 1 second

      const statusResult = await this.getExportJobStatus(jobId)

      if (!statusResult.success || !statusResult.data) {
        throw new ApiError('Falha ao verificar estado da exportação')
      }

      const { status, progress, error_message } = statusResult.data

      // Report progress if callback provided
      if (onProgress) {
        onProgress(progress, status)
      }

      if (status === 'completed') {
        // Download the file
        await this.downloadExportJob(jobId)
        return
      }

      if (status === 'failed') {
        throw new ApiError(error_message || 'A exportação falhou')
      }

      if (status === 'cancelled') {
        throw new ApiError('A exportação foi cancelada')
      }

      attempts++
    }

    throw new ApiError('Tempo limite excedido. Tente novamente.')
  },

  /**
   * Download Ajudas de Custo with async processing (for large exports with multiple colaboradores)
   * Shows progress and handles the background job automatically
   */
  async downloadAjudasCustoAsync(
    generationId: number,
    onProgress?: (progress: number, status: string) => void
  ): Promise<void> {
    // Start the async export job with ajudas_custo type
    const startResult = await this.startExportAsync(generationId, 'ajudas_custo')

    if (!startResult.success || !startResult.data?.job_id) {
      throw new ApiError(startResult.message || 'Falha ao iniciar exportação de Ajudas de Custo')
    }

    const jobId = startResult.data.job_id

    // Poll for completion
    const maxAttempts = 120 // 2 minutes max (1 second intervals)
    let attempts = 0

    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 1000)) // Wait 1 second

      const statusResult = await this.getExportJobStatus(jobId)

      if (!statusResult.success || !statusResult.data) {
        throw new ApiError('Falha ao verificar estado da exportação')
      }

      const { status, progress, error_message } = statusResult.data

      // Report progress if callback provided
      if (onProgress) {
        onProgress(progress, status)
      }

      if (status === 'completed') {
        // Download the file
        await this.downloadExportJob(jobId)
        return
      }

      if (status === 'failed') {
        throw new ApiError(error_message || 'A exportação de Ajudas de Custo falhou')
      }

      if (status === 'cancelled') {
        throw new ApiError('A exportação foi cancelada')
      }

      attempts++
    }

    throw new ApiError('Tempo limite excedido. Tente novamente.')
  },

  // ============================================================
  // ASYNC ROUTE GENERATION FUNCTIONS - Scalable background job processing
  // ============================================================

  /**
   * Start an async route generation job (returns immediately, processes in background)
   * This enables scalability for 10-100+ concurrent users
   */
  async startGenerationAsync(data: {
    target_amount: number
    radius_km?: number  // Optional - backend auto-determines optimal radius
    month: number
    year: number
  }): Promise<{
    success: boolean
    message: string
    data?: {
      job_id: string
      status: string
      poll_url: string
    }
  }> {
    return api.post('/api/routes/generate-async', data)
  },

  /**
   * Check the status of a route generation job
   */
  async getGenerationJobStatus(jobId: string): Promise<{
    success: boolean
    data?: {
      job_id: string
      status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled'
      progress: number
      current_step?: string
      total_colaboradores: number
      processed_colaboradores: number
      error_message?: string
      result?: {
        generation_id: number
        trips_generated: number
        total_kilometers: number
        total_cost: number
        colaboradores_count: number
      }
      created_at: string
      completed_at?: string
    }
  }> {
    return api.get(`/api/route-jobs/${jobId}/status`)
  },

  /**
   * Create route generation with async processing (for scalability with concurrent users)
   * Shows progress and handles the background job automatically
   */
  async createGenerationAsync(
    data: {
      target_amount: number
      radius_km?: number  // Optional - backend auto-determines optimal radius
      month: number
      year: number
    },
    onProgress?: (progress: number, status: string, stepInfo?: string) => void
  ): Promise<{ generation_id: number; trips_generated: number; total_kilometers: number; total_cost: number; colaboradores_count: number }> {
    // Start the async generation job
    const startResult = await this.startGenerationAsync(data)

    if (!startResult.success || !startResult.data?.job_id) {
      throw new ApiError(startResult.message || 'Falha ao iniciar geração de rotas')
    }

    const jobId = startResult.data.job_id

    // Poll for completion
    const maxAttempts = 300 // 5 minutes max (1 second intervals)
    let attempts = 0

    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 1000)) // Wait 1 second

      const statusResult = await this.getGenerationJobStatus(jobId)

      if (!statusResult.success || !statusResult.data) {
        throw new ApiError('Falha ao verificar estado da geração')
      }

      const { status, progress, current_step, error_message, result } = statusResult.data

      // Report progress if callback provided
      if (onProgress) {
        onProgress(progress, status, current_step)
      }

      if (status === 'completed' && result) {
        return result
      }

      if (status === 'failed') {
        throw new ApiError(error_message || 'A geração de rotas falhou')
      }

      if (status === 'cancelled') {
        throw new ApiError('A geração de rotas foi cancelada')
      }

      attempts++
    }

    throw new ApiError('Tempo limite excedido. Tente novamente.')
  },

  /**
   * Delete a route generation and all its associated trips
   * Requires ownership of the generation
   */
  async deleteGeneration(generationId: number): Promise<{ success: boolean; message: string }> {
    return api.delete(`/api/routes/${generationId}`)
  }
}

// Towns API
export const townsApi = {
  async getSelection(): Promise<Town[]> {
    return api.get('/api/v1/towns/selection')
  }
}

// Email collection API
export const emailApi = {
  async collectEmail(email: string): Promise<void> {
    return api.post('/api/email/collect', { email })
  }
}

export { ApiError }
